// constants
import { LIVE_UPDATE_RECONNECT_SPREAD_MS } from '../RosterPlan.consts';

// utils
import EventBus from 'lib/EventBusUpdated';

/**
 * Calls `catchUp` once after the bus reconnects, because whatever was published while the socket
 * was down is not resent and the app does not refetch on reconnect.
 *
 * A visible tab catches up at a random moment within `LIVE_UPDATE_RECONNECT_SPREAD_MS`: a restart
 * of the service behind the bus brings every client back at once, and the spread keeps all open
 * rosters from reloading in the same second.
 *
 * A hidden tab does not catch up at all until it is shown again. Browsers throttle the timers of a
 * background tab, the bridge misses the client's ping and closes the socket, and the client
 * reconnects — measured at about every 30 seconds in a hidden Chrome tab. Catching up on each of
 * those would reload the month for a screen nobody looks at, twice a minute per tab. So a reconnect
 * while hidden only records that a catch-up is due, and it runs once, straight away, when the tab
 * becomes visible: that moment is one planner's, so it needs no spread.
 *
 * Returns the function that stops listening.
 */
export const createReconnectCatchUp = (catchUp: () => void) => {
  let spreadTimer: ReturnType<typeof setTimeout> | undefined;
  let isDueOnVisible = false;

  const stopListening = EventBus.onReconnect(() => {
    if (document.hidden) {
      isDueOnVisible = true;
      return;
    }
    clearTimeout(spreadTimer);
    spreadTimer = setTimeout(
      catchUp,
      Math.random() * LIVE_UPDATE_RECONNECT_SPREAD_MS
    );
  });

  const onVisibilityChange = () => {
    if (document.hidden || !isDueOnVisible) return;
    isDueOnVisible = false;
    catchUp();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    stopListening();
    clearTimeout(spreadTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};
